/**
 * Microsoft Graph Configuration Service (WQ-204)
 *
 * Wraps /api/admin/microsoft-graph/* — the missing piece that was the
 * compounding cause of the 2026-04-18 SP sync diagnosis. Backend was
 * built in WQ-066; this is the client the new MicrosoftGraphConfigCard
 * uses.
 */

export interface GraphConfig {
  enabled: boolean;
  azure_tenant_id: string | null;
  client_id: string | null;
  display_name: string | null;
  sharepoint_enabled: boolean;
  graph_scopes: string[];
  has_client_secret: boolean;
  last_used_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface GraphConfigRequest {
  azure_tenant_id: string;
  client_id: string;
  client_secret?: string;
  display_name?: string;
  enabled?: boolean;
  sharepoint_enabled?: boolean;
  graph_scopes?: string[];
}

export interface GraphHealthResult {
  healthy: boolean;
  checks: Record<string, unknown>;
  message?: string;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

function tenantQs(tenantId?: string | null): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
}

export async function getGraphConfig(
  authFetch: FetchFn,
  tenantId?: string | null,
): Promise<GraphConfig> {
  const res = await authFetch(`/api/admin/microsoft-graph/config${tenantQs(tenantId)}`);
  if (!res.ok) throw new Error(`Failed to load Graph config: ${res.status}`);
  return res.json();
}

export async function saveGraphConfig(
  authFetch: FetchFn,
  config: GraphConfigRequest,
  tenantId?: string | null,
): Promise<{ message: string; enabled: boolean; azure_tenant_id: string }> {
  const res = await authFetch(
    `/api/admin/microsoft-graph/config${tenantQs(tenantId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save Graph config: ${res.status}`);
  }
  return res.json();
}

export async function testGraphConnection(
  authFetch: FetchFn,
  tenantId?: string | null,
): Promise<GraphHealthResult> {
  const res = await authFetch(
    `/api/admin/microsoft-graph/test${tenantQs(tenantId)}`,
    { method: 'POST' },
  );
  const body = await res.json().catch(() => ({}));

  // WQ-205 (iteration): the /test endpoint now returns 200 in every case
  // with `{healthy, message, checks}` — a probe reports state rather than
  // HTTP-erroring on unhealthy. The !res.ok branch below is defensive for
  // middleware failures (auth, rate-limit) where the body may instead
  // carry the platform error envelope `{error: {code, message, details}}`
  // (FastAPI default `{detail: ...}` also covered for completeness).
  if (!res.ok) {
    const err = body as {
      error?: { code?: string; message?: string };
      detail?: unknown;
    };
    let message = `HTTP ${res.status}`;
    if (err.error?.message) {
      message = err.error.message;
    } else if (typeof err.detail === 'string') {
      message = err.detail;
    } else if (err.detail && typeof err.detail === 'object') {
      const d = err.detail as { message?: string };
      if (d.message) message = d.message;
    }
    return { healthy: false, checks: {}, message };
  }

  // Normal path: body is `{healthy, message, checks}`.
  const parsed = body as {
    healthy?: boolean;
    message?: string;
    checks?: Record<string, unknown>;
  };
  return {
    healthy: parsed.healthy !== false, // default to true when omitted
    checks: parsed.checks || {},
    message: parsed.message || 'OK',
  };
}

export async function disableGraph(
  authFetch: FetchFn,
  tenantId?: string | null,
): Promise<{ message: string }> {
  const res = await authFetch(
    `/api/admin/microsoft-graph/disable${tenantQs(tenantId)}`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(`Failed to disable Graph: ${res.status}`);
  return res.json();
}
